(function() {
  var NAMESPACE = 'JafraTikTokCarousel';
  var SECTION_SELECTOR = '[data-tiktok-carousel-section]';
  var thumbnailCache = Object.create(null);
  var failedVideoIds = Object.create(null);
  // A player that hasn't sent any event in this many ms is treated as failed
  // (covers TikTok edge "Access Denied" pages, which never send postMessage).
  var PLAYER_READY_TIMEOUT = 12000;
  // Wait for scrolling to settle before loading players, so swiping past
  // slides doesn't fire a request for each one.
  var PLAYBACK_SETTLE_DELAY = 320;
  // Most players allowed at once. Visible slides play; everything else
  // (offscreen slides and loop copies) shows its poster.
  var MAX_ACTIVE_PLAYERS = 5;
  // A slide counts as visible when at least this share of its width is on screen.
  var VISIBLE_THRESHOLD = 0.6;

  function label(section, key, fallback) {
    if (!section) {
      return fallback;
    }
    var value = section.getAttribute('data-label-' + key);
    return value || fallback;
  }

  function extractVideoId(url) {
    if (!url) {
      return '';
    }

    var cleanedUrl = String(url).trim().split('?')[0].replace(/\/+$/, '');
    var match = cleanedUrl.match(/\/(?:video|photo)\/(\d+)$/i);
    return match ? match[1] : '';
  }

  function buildPlayerUrl(videoId, autoplay, muted) {
    // Only parameters documented at developers.tiktok.com/doc/embed-player.
    // Note: the mute parameter is "muted", not "mute".
    return 'https://www.tiktok.com/player/v1/' + videoId
      + '?autoplay=' + (autoplay ? '1' : '0')
      + '&muted=' + (muted ? '1' : '0')
      + '&loop=1&description=0&music_info=0&controls=1&native_context_menu=0'
      + '&fullscreen_button=0&rel=0';
  }

  function createIframe(videoId, autoplay, muted, titleLabel) {
    var iframe = document.createElement('iframe');
    iframe.src = buildPlayerUrl(videoId, autoplay, muted);
    iframe.setAttribute('allow', 'autoplay; fullscreen');
    iframe.setAttribute('loading', 'eager');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    iframe.setAttribute('title', titleLabel || 'TikTok video');
    iframe.setAttribute('scrolling', 'no');
    iframe.dataset.autoplay = autoplay ? '1' : '0';
    iframe.dataset.muted = muted ? '1' : '0';
    iframe.dataset.tiktokVideoId = videoId;
    return iframe;
  }

  function markVideoFailed(videoId, scope) {
    if (!videoId) {
      return;
    }
    failedVideoIds[videoId] = true;
    var root = scope || document;
    root.querySelectorAll('.tiktok-carousel__slide').forEach(function(slide) {
      if (slide.dataset.tiktokVideoId === videoId) {
        unloadSlideVideo(slide);
        slide.classList.add('is-failed');
      }
    });
  }

  function watchPlayerReady(iframe, section) {
    window.setTimeout(function() {
      if (iframe.isConnected && iframe.dataset.tiktokReady !== 'true') {
        markVideoFailed(iframe.dataset.tiktokVideoId, section);
      }
    }, PLAYER_READY_TIMEOUT);
  }

  // TikTok's player reports readiness and errors via postMessage.
  function handlePlayerMessage(event) {
    if (!event.origin || event.origin.indexOf('tiktok.com') === -1) {
      return;
    }

    var data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (error) {
        return;
      }
    }
    if (!data || !data['x-tiktok-player']) {
      return;
    }

    var sourceIframe = null;
    document.querySelectorAll(SECTION_SELECTOR + ' iframe').forEach(function(iframe) {
      if (iframe.contentWindow === event.source) {
        sourceIframe = iframe;
      }
    });
    if (!sourceIframe) {
      return;
    }

    if (data.type !== 'onPlayerError' && data.type !== 'onError') {
      // Any event from the player (ready, state change, time update) proves it loaded.
      sourceIframe.dataset.tiktokReady = 'true';
      return;
    }

    var code = data.value && data.value.errorCode;
    // 3002 = browser blocked autoplay. The video is fine; the shopper can press play.
    if (code === 3002) {
      sourceIframe.dataset.tiktokReady = 'true';
      return;
    }
    markVideoFailed(sourceIframe.dataset.tiktokVideoId, sourceIframe.closest(SECTION_SELECTOR));
  }

  function fetchThumbnailUrl(videoUrl) {
    if (!videoUrl) {
      return Promise.resolve('');
    }

    // Cache the promise, not just the result, so duplicate slides share one request.
    if (thumbnailCache[videoUrl]) {
      return thumbnailCache[videoUrl];
    }

    var oEmbedUrl = 'https://www.tiktok.com/oembed?url=' + encodeURIComponent(videoUrl);
    thumbnailCache[videoUrl] = fetch(oEmbedUrl, {method: 'GET'})
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Could not fetch TikTok thumbnail');
        }
        return response.json();
      })
      .then(function(payload) {
        return payload && payload.thumbnail_url ? String(payload.thumbnail_url) : '';
      })
      .catch(function() {
        return '';
      });
    return thumbnailCache[videoUrl];
  }

  function applyPosterImage(slide, src) {
    if (!slide || !src) {
      return;
    }

    var posterImage = slide.querySelector('[data-tiktok-poster-image]');
    if (!posterImage) {
      return;
    }

    posterImage.src = src;
    posterImage.setAttribute('loading', 'eager');
    if (posterImage.decode) {
      posterImage.decode().catch(function() {});
    }
  }

  function hydrateSlidePoster(slide) {
    if (!slide) {
      return Promise.resolve();
    }

    var videoUrl = slide.dataset.tiktokVideoId ? slide.getAttribute('data-tiktok-video-url') : '';
    var fallbackPoster = slide.getAttribute('data-tiktok-fallback-poster') || '';

    if (fallbackPoster) {
      applyPosterImage(slide, fallbackPoster);
    }

    return fetchThumbnailUrl(videoUrl).then(function(thumbnailUrl) {
      if (thumbnailUrl) {
        applyPosterImage(slide, thumbnailUrl);
      }
    });
  }

  function hydrateAllPosters(section) {
    if (!section) {
      return;
    }

    var slides = Array.prototype.slice.call(section.querySelectorAll('.tiktok-carousel__slide'));
    slides.forEach(function(slide) {
      hydrateSlidePoster(slide);
    });
  }

  function ensureSlideIframe(slide, autoplay, muted) {
    var embedContainer = slide.querySelector('[data-tiktok-embed]');
    var videoId = slide.dataset.tiktokVideoId;
    if (!embedContainer || !videoId || failedVideoIds[videoId]) {
      return null;
    }

    var section = slide.closest(SECTION_SELECTOR);
    var iframe = embedContainer.querySelector('iframe');
    if (!iframe) {
      iframe = createIframe(videoId, autoplay, muted !== false, label(section, 'video', 'TikTok video'));
      embedContainer.appendChild(iframe);
      watchPlayerReady(iframe, section);
    }

    embedContainer.classList.add('is-playing');
    return iframe;
  }

  function unloadSlideVideo(slide) {
    if (!slide) {
      return;
    }

    var embedContainer = slide.querySelector('[data-tiktok-embed]');
    if (!embedContainer) {
      return;
    }

    var iframe = embedContainer.querySelector('iframe');
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
    embedContainer.classList.remove('is-playing');
  }

  function updateButtons(track, prevButton, nextButton) {
    if (!track || !prevButton || !nextButton) {
      return;
    }

    var isLoopEnabled = track.dataset.tiktokLoopEnabled === 'true';
    if (isLoopEnabled) {
      prevButton.disabled = false;
      nextButton.disabled = false;
      return;
    }

    var maxScrollLeft = Math.max(track.scrollWidth - track.clientWidth, 0);
    prevButton.disabled = track.scrollLeft <= 2;
    nextButton.disabled = track.scrollLeft >= maxScrollLeft - 2;
  }

  function setInstantScrollLeft(track, left) {
    if (!track) {
      return;
    }

    var previousBehavior = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = left;

    window.requestAnimationFrame(function() {
      track.style.scrollBehavior = previousBehavior;
    });
  }

  function centerSlideInTrack(track, slide) {
    if (!track || !slide) {
      return;
    }

    var targetScrollLeft = slide.offsetLeft - ((track.clientWidth - slide.offsetWidth) / 2);
    setInstantScrollLeft(track, Math.max(targetScrollLeft, 0));
  }

  function scrollToSlide(track, slide, behavior) {
    if (!track || !slide) {
      return;
    }

    var targetScrollLeft = slide.offsetLeft - ((track.clientWidth - slide.offsetWidth) / 2);
    track.scrollTo({
      left: Math.max(targetScrollLeft, 0),
      behavior: behavior || 'auto'
    });
  }

  function getCenteredSlide(track, slides) {
    var slidesToCheck = slides || Array.prototype.slice.call(track.querySelectorAll('.tiktok-carousel__slide'));
    if (!slidesToCheck.length) {
      return null;
    }

    var centerX = track.scrollLeft + (track.clientWidth / 2);
    var closestIndex = 0;
    var closestDistance = Number.POSITIVE_INFINITY;

    slidesToCheck.forEach(function(slide, index) {
      var slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
      var distance = Math.abs(centerX - slideCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return {
      slide: slidesToCheck[closestIndex],
      index: closestIndex
    };
  }

  function updateNavAlignment(section, referenceSlide) {
    var viewport = section.querySelector('.tiktok-carousel__viewport');
    if (!viewport) {
      return;
    }

    var targetSlide = referenceSlide
      || section.querySelector('.tiktok-carousel__slide.is-center')
      || section.querySelector('.tiktok-carousel__slide');
    if (!targetSlide) {
      return;
    }

    var embed = targetSlide.querySelector('[data-tiktok-embed]');
    if (!embed) {
      return;
    }

    var viewportRect = viewport.getBoundingClientRect();
    var embedRect = embed.getBoundingClientRect();
    var top = (embedRect.top - viewportRect.top) + (embedRect.height / 2);

    if (!Number.isFinite(top) || top <= 0) {
      return;
    }

    viewport.style.setProperty('--tiktok-nav-top', String(top) + 'px');
  }

  function updateDots(section, activeIndex) {
    var dots = section.querySelectorAll('[data-tiktok-dot-index]');
    if (!dots.length) {
      return;
    }

    dots.forEach(function(dot) {
      var dotIndex = Number(dot.getAttribute('data-tiktok-dot-index'));
      var isActive = dotIndex === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  function setupDots(section) {
    var track = section.querySelector('[data-tiktok-track]');
    var dotsContainer = section.querySelector('[data-tiktok-dots]');
    if (!track || !dotsContainer || dotsContainer.dataset.tiktokDotsReady === 'true') {
      return;
    }

    var originals = Array.prototype.slice.call(track.querySelectorAll('.tiktok-carousel__slide[data-tiktok-original="true"]'));
    if (!originals.length) {
      originals = Array.prototype.slice.call(track.querySelectorAll('.tiktok-carousel__slide'));
    }

    if (!originals.length) {
      return;
    }

    dotsContainer.dataset.tiktokDotsReady = 'true';
    dotsContainer.innerHTML = '';

    originals.forEach(function(_, index) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'tiktok-carousel__dot';
      dot.setAttribute('data-tiktok-dot-index', String(index));
      dot.setAttribute('aria-label', label(section, 'goto', 'Go to video') + ' ' + String(index + 1));
      dot.addEventListener('click', function() {
        var target = track.querySelector(
          '.tiktok-carousel__slide[data-tiktok-original="true"][data-tiktok-loop-index="' + index + '"]'
        ) || track.querySelector('.tiktok-carousel__slide[data-tiktok-loop-index="' + index + '"]');
        if (target) {
          scrollToSlide(track, target, 'smooth');
        }
      });
      dotsContainer.appendChild(dot);
    });

    updateDots(section, 0);
  }

  function setupInfiniteLoop(section) {
    var track = section.querySelector('[data-tiktok-track]');
    if (!track || track.dataset.tiktokLoopReady === 'true') {
      return;
    }

    var originals = Array.prototype.slice.call(track.querySelectorAll('.tiktok-carousel__slide'));
    if (originals.length < 2) {
      return;
    }

    originals.forEach(function(slide, index) {
      slide.dataset.tiktokLoopIndex = String(index);
      slide.dataset.tiktokOriginal = 'true';
    });

    var prependClones = [];
    originals.forEach(function(slide) {
      var clone = slide.cloneNode(true);
      clone.classList.add('is-clone');
      clone.dataset.tiktokOriginal = 'false';
      prependClones.push(clone);
    });

    var appendClones = [];
    originals.forEach(function(slide) {
      var clone = slide.cloneNode(true);
      clone.classList.add('is-clone');
      clone.dataset.tiktokOriginal = 'false';
      appendClones.push(clone);
    });

    prependClones.reverse().forEach(function(clone) {
      track.insertBefore(clone, track.firstChild);
    });
    appendClones.forEach(function(clone) {
      track.appendChild(clone);
    });

    track.dataset.tiktokLoopEnabled = 'true';
    track.dataset.tiktokLoopReady = 'true';

    window.requestAnimationFrame(function() {
      var firstOriginal = track.querySelector('.tiktok-carousel__slide[data-tiktok-original="true"]');
      centerSlideInTrack(track, firstOriginal);
    });
  }

  function bindScrollControls(section) {
    var track = section.querySelector('[data-tiktok-track]');
    var prevButton = section.querySelector('[data-tiktok-prev]');
    var nextButton = section.querySelector('[data-tiktok-next]');
    if (!track || !prevButton || !nextButton) {
      return;
    }

    var getSlides = function() {
      return Array.prototype.slice.call(track.querySelectorAll('.tiktok-carousel__slide'));
    };

    var getTargetForDirection = function(currentSlide, direction, slides) {
      if (!currentSlide || !slides || !slides.length) {
        return null;
      }

      var originals = slides.filter(function(slide) {
        return slide.dataset.tiktokOriginal === 'true';
      });
      var total = originals.length || slides.length;
      var currentLoopIndex = Number(currentSlide.dataset.tiktokLoopIndex || 0);
      var nextLoopIndex = (currentLoopIndex + direction + total) % total;
      var currentLeft = currentSlide.offsetLeft;

      var candidates = slides.filter(function(slide) {
        return Number(slide.dataset.tiktokLoopIndex || -1) === nextLoopIndex;
      });

      if (!candidates.length) {
        return null;
      }

      var directional = candidates
        .map(function(slide) {
          return {
            slide: slide,
            delta: slide.offsetLeft - currentLeft
          };
        })
        .filter(function(item) {
          return direction > 0 ? item.delta > 0 : item.delta < 0;
        })
        .sort(function(a, b) {
          return Math.abs(a.delta) - Math.abs(b.delta);
        });

      if (directional.length) {
        return directional[0].slide;
      }

      return candidates[0];
    };

    var isNavigating = false;

    var navigateByArrow = function(direction) {
      if (isNavigating) {
        return;
      }

      var slides = getSlides();
      if (!slides.length) {
        return;
      }

      var centered = getCenteredSlide(track, slides);
      if (!centered || !centered.slide) {
        return;
      }

      var targetSlide = getTargetForDirection(centered.slide, direction, slides);
      if (!targetSlide) {
        return;
      }

      isNavigating = true;
      scrollToSlide(track, targetSlide, 'smooth');
      window.setTimeout(function() {
        isNavigating = false;
      }, 420);
    };

    prevButton.addEventListener('click', function() {
      navigateByArrow(-1);
    });

    nextButton.addEventListener('click', function() {
      navigateByArrow(1);
    });

    track.addEventListener('scroll', function() {
      window.requestAnimationFrame(function() {
        updateButtons(track, prevButton, nextButton);
      });
    }, {passive: true});

    window.addEventListener('resize', function() {
      updateButtons(track, prevButton, nextButton);
    });

    updateButtons(track, prevButton, nextButton);
  }

  // Slides with at least VISIBLE_THRESHOLD of their width inside the track.
  function getVisibleSlides(track) {
    var trackRect = track.getBoundingClientRect();
    return Array.prototype.slice.call(track.querySelectorAll('.tiktok-carousel__slide'))
      .filter(function(slide) {
        var r = slide.getBoundingClientRect();
        var shown = Math.min(r.right, trackRect.right) - Math.max(r.left, trackRect.left);
        return r.width > 0 && shown / r.width >= VISIBLE_THRESHOLD;
      });
  }

  // Autoplay mode: every visible slide gets a muted player, up to
  // MAX_ACTIVE_PLAYERS. Offscreen slides and loop copies show their poster,
  // which keeps the request count to TikTok low enough to avoid blocking.
  function playVisibleSlides(section) {
    if (!section || section.dataset.tiktokInView !== 'true') {
      return;
    }

    var track = section.querySelector('[data-tiktok-track]');
    if (!track) {
      return;
    }

    var visible = getVisibleSlides(track).slice(0, MAX_ACTIVE_PLAYERS);
    track.querySelectorAll('.tiktok-carousel__slide').forEach(function(slide) {
      if (visible.indexOf(slide) === -1) {
        unloadSlideVideo(slide);
      }
    });

    visible.forEach(function(slide) {
      if (slide.dataset.tiktokVideoId) {
        ensureSlideIframe(slide, true, true);
      }
    });
  }

  function unloadAllSlides(section) {
    section.querySelectorAll('.tiktok-carousel__slide').forEach(unloadSlideVideo);
  }

  // Load nothing until the section is near the screen; unload when it leaves.
  function observeVisibility(section, onEnter) {
    if (!('IntersectionObserver' in window)) {
      section.dataset.tiktokInView = 'true';
      onEnter();
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          section.dataset.tiktokInView = 'true';
          onEnter();
        } else {
          section.dataset.tiktokInView = 'false';
          unloadAllSlides(section);
        }
      });
    }, {rootMargin: '200px 0px'});

    observer.observe(section);
  }

  // Click to play mode (autoplay off): one player at a time, with sound.
  function activateSlide(section, slide) {
    var track = section.querySelector('[data-tiktok-track]');
    if (!track || !slide) {
      return;
    }

    var allSlides = Array.prototype.slice.call(track.querySelectorAll('.tiktok-carousel__slide'));
    allSlides.forEach(function(item) {
      if (item !== slide) {
        unloadSlideVideo(item);
      }
    });

    slide.dataset.tiktokActivated = 'true';
    ensureSlideIframe(slide, true, false);
    scrollToSlide(track, slide, 'smooth');
  }

  function bindPlayButtons(section) {
    var track = section.querySelector('[data-tiktok-track]');
    if (!track) {
      return;
    }

    track.querySelectorAll('[data-tiktok-play]').forEach(function(button) {
      if (button.dataset.tiktokPlayBound === 'true') {
        return;
      }

      button.dataset.tiktokPlayBound = 'true';
      button.addEventListener('click', function() {
        var slide = button.closest('.tiktok-carousel__slide');
        if (!slide) {
          return;
        }
        activateSlide(section, slide);
      });
    });
  }

  function setAddStatus(statusNode, message, type) {
    if (!statusNode) {
      return;
    }

    statusNode.textContent = message || '';
    statusNode.classList.remove('is-success');
    statusNode.classList.remove('is-visible');

    if (!message) {
      return;
    }

    if (type === 'success') {
      statusNode.classList.add('is-success');
    }
    statusNode.classList.add('is-visible');
  }

  function bindAddToCartButtons(section) {
    if (!section || section.dataset.tiktokAddBound === 'true') {
      return;
    }

    section.dataset.tiktokAddBound = 'true';

    section.addEventListener('click', function(event) {
      var button = event.target.closest('[data-tiktok-add]');
      if (!button || !section.contains(button)) {
        return;
      }

      event.preventDefault();

      var variantId = Number(button.getAttribute('data-variant-id'));
      var defaultLabel = button.getAttribute('data-default-label') || '+';
      var statusNode = button.parentElement ? button.parentElement.querySelector('[data-tiktok-add-status]') : null;

      if (!variantId) {
        setAddStatus(statusNode, label(section, 'unavailable', 'Product unavailable.'));
        return;
      }

      setAddStatus(statusNode, '');
      button.disabled = true;
      button.classList.add('loading');
      button.textContent = '...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1
            }
          ]
        })
      })
        .then(function(response) {
          if (!response.ok) {
            return response.text().then(function(errorText) {
              throw new Error(errorText || 'Could not add to cart');
            });
          }
          return response.json();
        })
        .then(function() {
          setAddStatus(statusNode, label(section, 'added', 'Added to cart.'), 'success');
          document.dispatchEvent(new CustomEvent('show-personal-cart'));
          document.dispatchEvent(new CustomEvent('theme:cart:init', {bubbles: true}));
          document.dispatchEvent(new CustomEvent('theme:cart:reload', {bubbles: true}));
          button.textContent = '✓';
        })
        .catch(function(error) {
          console.error('TikTok add-to-cart error:', error);
          setAddStatus(statusNode, label(section, 'add-error', 'Could not add to cart.'));
          button.textContent = defaultLabel;
        })
        .finally(function() {
          button.classList.remove('loading');
          window.setTimeout(function() {
            button.disabled = defaultLabel === 'x';
            button.textContent = defaultLabel;
          }, 900);
        });
    });
  }

  function bindCenteredPlayback(section) {
    var track = section.querySelector('[data-tiktok-track]');
    if (!track) {
      return;
    }

    var rafId = null;
    var scrollEndTimer = null;
    var isRepositioning = false;
    var currentCenterKey = '';
    var playbackTimer = null;
    var autoplayEnabled = section.dataset.videoAutoplay === 'true';

    var schedulePlayback = function() {
      if (!autoplayEnabled) {
        return;
      }
      window.clearTimeout(playbackTimer);
      playbackTimer = window.setTimeout(function() {
        if (!isRepositioning) {
          playVisibleSlides(section);
        }
      }, PLAYBACK_SETTLE_DELAY);
    };
    section.tiktokSchedulePlayback = schedulePlayback;

    var updateCenteredSlide = function() {
      if (isRepositioning) {
        return;
      }

      rafId = null;
      var slides = Array.prototype.slice.call(track.querySelectorAll('.tiktok-carousel__slide'));
      if (!slides.length) {
        return;
      }

      var centered = getCenteredSlide(track, slides);
      if (!centered || !centered.slide) {
        return;
      }

      var centeredSlide = centered.slide;
      var centerKey = centeredSlide.dataset.tiktokLoopIndex + ':' + String(centeredSlide.classList.contains('is-clone'));
      updateNavAlignment(section, centeredSlide);
      updateDots(section, Number(centeredSlide.dataset.tiktokLoopIndex || centered.index || 0));

      if (centerKey === currentCenterKey) {
        return;
      }

      slides.forEach(function(slide, index) {
        var isCenter = index === centered.index;
        slide.classList.toggle('is-center', isCenter);
        slide.classList.toggle('is-left', index < centered.index);
        slide.classList.toggle('is-right', index > centered.index);
      });

      currentCenterKey = centerKey;
      schedulePlayback();
    };

    var handleScrollEnd = function() {
      if (isRepositioning) {
        return;
      }

      var isLoopEnabled = track.dataset.tiktokLoopEnabled === 'true';
      if (!isLoopEnabled) {
        return;
      }

      var slides = Array.prototype.slice.call(track.querySelectorAll('.tiktok-carousel__slide'));
      var centered = getCenteredSlide(track, slides);
      if (!centered || !centered.slide || !centered.slide.classList.contains('is-clone')) {
        return;
      }

      var loopIndex = centered.slide.dataset.tiktokLoopIndex;
      var targetOriginal = track.querySelector(
        '.tiktok-carousel__slide[data-tiktok-original="true"][data-tiktok-loop-index="' + loopIndex + '"]'
      );

      if (!targetOriginal || targetOriginal === centered.slide) {
        return;
      }

      isRepositioning = true;
      setInstantScrollLeft(
        track,
        targetOriginal.offsetLeft - ((track.clientWidth - targetOriginal.offsetWidth) / 2)
      );

      window.requestAnimationFrame(function() {
        isRepositioning = false;
        updateCenteredSlide();
      });
    };

    var scheduleUpdate = function() {
      if (isRepositioning) {
        return;
      }

      if (!rafId) {
        rafId = window.requestAnimationFrame(updateCenteredSlide);
      }

      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(handleScrollEnd, 140);
    };

    track.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    // A resize can change how many slides fit on screen without moving the center.
    window.addEventListener('resize', schedulePlayback);
    window.requestAnimationFrame(scheduleUpdate);
    window.setTimeout(scheduleUpdate, 30);
  }

  function initSection(section) {
    if (!section || section.dataset.tiktokCarouselReady === 'true') {
      return;
    }

    section.dataset.tiktokCarouselReady = 'true';

    var slides = section.querySelectorAll('.tiktok-carousel__slide');
    slides.forEach(function(slide, index) {
      var url = slide.getAttribute('data-tiktok-video-url');
      var videoId = extractVideoId(url);
      var fallback = slide.querySelector('[data-tiktok-fallback]');

      if (!videoId) {
        slide.classList.add('is-invalid');
        if (fallback) {
          fallback.hidden = false;
        }
        return;
      }

      slide.dataset.tiktokVideoId = videoId;
      slide.dataset.tiktokLoopIndex = String(index);
      slide.dataset.tiktokOriginal = 'true';
    });

    var autoplayEnabled = section.dataset.videoAutoplay === 'true';

    setupInfiniteLoop(section);

    if (!autoplayEnabled) {
      bindPlayButtons(section);
    }

    hydrateAllPosters(section);
    setupDots(section);
    bindScrollControls(section);
    bindAddToCartButtons(section);
    bindCenteredPlayback(section);

    observeVisibility(section, function() {
      if (autoplayEnabled && section.tiktokSchedulePlayback) {
        section.tiktokSchedulePlayback();
      }
    });
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (!window[NAMESPACE]) {
    window[NAMESPACE] = {
      initAll: initAll
    };

    document.addEventListener('shopify:section:load', function(event) {
      initAll(event.target);
    });

    window.addEventListener('message', handlePlayerMessage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      window[NAMESPACE].initAll(document);
    });
  } else {
    window[NAMESPACE].initAll(document);
  }
})();
